import { lazy, Suspense, useEffect, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import useSWR from "swr";
import { RouteFallback } from "@/components/route-fallback";
import { reportEvent } from "@/utils/observability";
import {
  loadProposal,
  proposalSwrKey,
} from "../proposal-standard/loadProposal";

const standardPageModule = async () =>
  import("../proposal-standard/ProposalPage");
const StandardProposalPage = lazy(standardPageModule);
const ProposalUnavailablePage = lazy(
  async () => import("../proposal-standard/ProposalUnavailablePage"),
);

const SMB_ALIAS_PREFIX = "/vision/proposta-smb";

type ProposalTemplate = "standard" | "unavailable";

/**
 * The single client-facing viewer for /vision/proposta/:uuid (and its
 * /vision/proposta-smb alias, kept so already-sent SMB links never break):
 * fetches the proposal first and picks the template by its own
 * `proposal_type` — the type is data, not something a URL can be trusted to
 * carry (PED-12653).
 *
 * Typed proposals (SMB or ENT) render the standard template — ENT gets its
 * extra pricing rows via ProposalVariantProvider inside the page. An untyped
 * (or unknown-typed) proposal gets the "not found or expired" page: the old
 * template that understood that payload was removed (PED-14203), and only a
 * handful of such proposals existed, none created by a live flow. The
 * /proposta-smb alias keeps rendering the standard page whatever the payload
 * says — every link ever sent on that path was the standard page. `preview`
 * and `preview-ent` render the standard template's mock on either path.
 *
 * The SWR key is shared with the standard page's own live flow, so this
 * fetch IS the page's fetch (deduped) — picking a template costs no second
 * request. A load error also goes to the standard page: its cover screen
 * owns the retry UX, off the same shared key.
 *
 * Keyed by uuid: the router reuses this element across /proposta/A →
 * /proposta/B, and the template latch below is per proposal — without a
 * remount, B would inherit A's template (and an "unavailable" A would report
 * B as an untyped hit).
 */
export default function ProposalRoutePage() {
  const { uuid } = useParams<{ uuid: string }>();
  return <ProposalTemplateResolver key={uuid} uuid={uuid} />;
}

function ProposalTemplateResolver({ uuid }: { uuid: string | undefined }) {
  const { pathname } = useLocation();
  const isSmbAlias = pathname.startsWith(SMB_ALIAS_PREFIX);
  // The internal design-review render — no network, each path's own mock.
  const isPreview = uuid === "preview" || uuid === "preview-ent";

  // Download the common-case (standard) chunk while the proposal fetch is in
  // flight instead of serializing chunk behind fetch — both are lazy, so
  // without this every cold open pays fetch + chunk back to back.
  useEffect(() => {
    void standardPageModule();
  }, []);

  const { data: proposal, error } = useSWR(
    !isPreview && uuid ? proposalSwrKey(uuid) : null,
    () => loadProposal(uuid!),
  );

  const resolved = isPreview || proposal !== undefined || error != null;
  // Unavailable only on the canonical path, for a payload that positively
  // exists and has no type — everything else (typed, alias, preview,
  // not-found, load error) is the standard page's to handle.
  const isUntyped =
    !isSmbAlias &&
    proposal != null &&
    proposal.proposalType !== "SMB" &&
    proposal.proposalType !== "ENT";

  // Latched on the first resolution and never re-decided: SWR keeps
  // revalidating this always-mounted hook (focus, error retry), and a late
  // result flipping the template would unmount the page mid-interaction —
  // wiping a half-typed email gate. Whatever the client saw first, they keep.
  const decidedTemplateRef = useRef<ProposalTemplate | null>(null);
  if (resolved && decidedTemplateRef.current == null) {
    decidedTemplateRef.current =
      !isPreview && isUntyped ? "unavailable" : "standard";
  }
  const template = decidedTemplateRef.current;

  // Datadog signal for every hit, so a link nobody expected to still be
  // opened shows up instead of failing silently.
  useEffect(() => {
    if (template === "unavailable") {
      reportEvent("proposal_untyped_unavailable", {
        operation: "getProposal",
        proposalId: uuid,
      });
    }
  }, [template, uuid]);

  return (
    <Suspense fallback={<RouteFallback variant="proposal" />}>
      {template == null ? (
        <RouteFallback variant="proposal" />
      ) : template === "unavailable" ? (
        <ProposalUnavailablePage />
      ) : (
        <StandardProposalPage />
      )}
    </Suspense>
  );
}
